import { Menu } from 'antd';
import React from 'react';
import './left-menu.less';
import { useStore } from '../../../store';
import RightOutlined from '@ant-design/icons/lib/icons/RightOutlined';
import DownOutlined from '@ant-design/icons/lib/icons/DownOutlined';
import { CompleteIcon, ProgressIcon } from '../Widget';
import { MenuInfo } from 'rc-menu/lib/interface';
import { useNavigate, useParams } from 'react-router-dom';
import { useCourseStore } from '../../courseStore';
import { getCourseThemeColor } from '../../courseTheme';
interface ICourseOverviewProps {
  steps: string[];
  currentStep: number;
  topics: any[];
}

export interface ICourseStep {
  title: string;
  link: string;
  state: 'active' | 'completed' | 'none';
}
const scrollCourseTop = (behavior?: any) => {
  document?.querySelector('.course-body')?.scrollTo({
    top: 0,
    left: 0,
    behavior,
  });
};
const scrollParagraphIntoView = (index: number) => {
  document
    .querySelectorAll('[data-paragraph]')
    [index]?.scrollIntoView({ behavior: 'smooth' });
};
const StepItem = ({ step, fill }: { step: any; fill: string }) => (
  <div
    className={`step-dot ${step.state}-dot`}
    onClick={() => {
      if (step.state !== 'none') {
        scrollCourseTop('smooth');
      }
    }}
  >
    {step.state === 'completed' && <CompleteIcon fill={fill} />}
  </div>
);
export const LeftMenu: React.FunctionComponent<ICourseOverviewProps> = ({
  steps,
  currentStep,
  topics,
}) => {
  const param = useParams();
  const p_course = param.course || '';
  const p_topic = param['*'] || 'welcome';
  // 左侧进度勾选栏（步骤圆点、子项完成/进行中标记）统一跟随课程主题色
  const themeColor = getCourseThemeColor(p_course);
  const { language, getTopicState } = useStore();
  const { activeParagraph } = useCourseStore();
  const topicState = getTopicState(p_course);
  const navigate = useNavigate();

  // 使用 useMemo 同步计算选中状态，避免延迟
  const currentSelectedKey = React.useMemo(
    () => `${p_topic}-${activeParagraph}`,
    [p_topic, activeParagraph]
  );
  const currentOpenKey = React.useMemo(() => p_topic, [p_topic]);

  const [openKeys, setOpenKeys] = React.useState([currentOpenKey]);
  const [selectedKeys, setsSelectedKeys] = React.useState([currentSelectedKey]);
  const onMenu = (e: MenuInfo) => {
    const arr = e.key?.split('-');
    const topicKey = arr[0];
    const paragraphIndex = Number(arr[1]);
    const isCurrentTopic = topicKey === p_topic;

    // 立即更新选中状态，避免延迟
    setsSelectedKeys([`${topicKey}-${arr[1]}`]);
    if (!isCurrentTopic) {
      setOpenKeys([topicKey]);
      navigate(`/${language}/course/${p_course}/${topicKey}`);
    }

    if (isCurrentTopic) {
      scrollParagraphIntoView(paragraphIndex);
    } else if (paragraphIndex === 0) {
      scrollCourseTop();
    } else {
      setTimeout(() => scrollParagraphIntoView(paragraphIndex), 200);
    }
  };
  const onOpenChange: any = (keys: any) => {
    if (keys.length > 0) {
      // 只允许一个子菜单展开，所以将openKeys设置为当前点击的子菜单的key
      setOpenKeys([keys[keys.length - 1]]);
    } else {
      // 如果没有子菜单展开，设置openKeys为空数组
      setOpenKeys([]);
    }
  };
  const expandIcon = ({ isOpen }: any) =>
    isOpen ? <DownOutlined /> : <RightOutlined />;
  const [menuItems, setMenuItems] = React.useState<any[]>([]);

  React.useEffect(() => {
    const updatedSteps = steps.map((step: any, index: number) => ({
      ...step,
      state:
        topicState[index]?.state === 'completed'
          ? 'completed'
          : topicState[index]?.state === 'in-progress' || index === currentStep
          ? 'active'
          : 'none',
      children: topics[index]?.paragraphs?.map(
        (subItem: any, subIndex: number) => ({
          ...subItem,
          state:
            step.link === p_topic && subIndex === activeParagraph
              ? 'active'
              : topicState[index]?.paragraphStates?.[subIndex]?.state,
        })
      ),
    }));

    // 转换为Menu items格式
    const items = updatedSteps.map((step: any) => ({
      key: step.link,
      icon: <StepItem step={step} fill={themeColor} />,
      label: step.title,
      disabled: step.state === 'none',
      className: step.state,
      children: step?.children?.map((child: any, subIndex: number) => ({
        key: `${step.link}-${subIndex}`,
        label: (
          <>
            <span className="item-word">{child?.title}</span>
            <span className={`item-dot ${child?.state}`}>
              {child?.state === 'completed' && (
                <CompleteIcon fill={themeColor} />
              )}
              {child?.state === 'in-progress' && (
                <ProgressIcon fill={themeColor} />
              )}
            </span>
          </>
        ),
        disabled: child?.state === 'not-start',
      })),
    }));

    // 课程配置中标记 hide 的步骤不在左侧菜单显示（items 与 steps 索引一一对应，仅过滤显示）
    setMenuItems(items.filter((_, index) => !(steps as any[])?.[index]?.hide));
  }, [
    steps,
    currentStep,
    topics,
    topicState,
    activeParagraph,
    p_topic,
    themeColor,
  ]);

  // 单独监听路由变化，立即更新菜单状态，避免延迟
  React.useEffect(() => {
    setsSelectedKeys([currentSelectedKey]);
    setOpenKeys([currentOpenKey]);
  }, [currentSelectedKey, currentOpenKey]);

  return (
    <Menu
      // left-menu-course-theme 用于提升样式优先级，避免 civil 页面同名全局样式覆盖本课程菜单配色
      className="left-menu left-menu-course-theme"
      // 注入主题色 CSS 变量，供 left-menu.less 中圆点边框/背景统一取色
      style={{ '--course-theme-color': themeColor } as React.CSSProperties}
      onClick={onMenu}
      openKeys={openKeys}
      selectedKeys={selectedKeys}
      mode="inline"
      expandIcon={expandIcon}
      onOpenChange={onOpenChange}
      items={menuItems}
    />
  );
};
